import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  subWeeks,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format,
  parseISO,
} from "date-fns";
import { nl } from "date-fns/locale";

interface WeekPoint {
  week: string;
  voltooid: number;
}

interface MonthPoint {
  month: string;
  nieuw: number;
  opgelost: number;
}

interface CalibrationSlice {
  name: string;
  value: number;
  color: string;
}

type Range = "8w" | "12w";
type MonthRange = "3m" | "6m";

const KpiCharts: React.FC = () => {
  const { selectedPharmacy } = useAuth();
  const [taskRange, setTaskRange] = useState<Range>("8w");
  const [complaintRange, setComplaintRange] = useState<MonthRange>("6m");
  const [taskTrend, setTaskTrend] = useState<WeekPoint[]>([]);
  const [complaintTrend, setComplaintTrend] = useState<MonthPoint[]>([]);
  const [calibrationStats, setCalibrationStats] = useState<CalibrationSlice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedPharmacy) fetchTrendData();
  }, [selectedPharmacy, taskRange, complaintRange]);

  const fetchTrendData = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      await Promise.all([
        fetchTaskTrend(),
        fetchComplaintTrend(),
        fetchCalibrationStats(),
      ]);
    } catch (err) {
      console.error("KPI chart fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskTrend = async () => {
    const weeks = taskRange === "8w" ? 8 : 12;
    const since = subWeeks(new Date(), weeks);

    const { data } = await supabase
      .from(Tables.TASKS)
      .select("completed_at")
      .eq("pharmacy_id", selectedPharmacy!.id)
      .eq("status", "completed")
      .gte("completed_at", since.toISOString())
      .not("completed_at", "is", null);

    // Build week buckets
    const points: WeekPoint[] = [];
    for (let i = weeks - 1; i >= 0; i--) {
      const weekStart = startOfWeek(subWeeks(new Date(), i), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const label = format(weekStart, "d MMM", { locale: nl });
      const count = (data ?? []).filter((t) => {
        if (!t.completed_at) return false;
        const d = parseISO(t.completed_at);
        return d >= weekStart && d <= weekEnd;
      }).length;
      points.push({ week: label, voltooid: count });
    }
    setTaskTrend(points);
  };

  const fetchComplaintTrend = async () => {
    const months = complaintRange === "3m" ? 3 : 6;
    const since = subMonths(new Date(), months);

    const { data } = await supabase
      .from(Tables.COMPLAINTS)
      .select("created_at, resolved_at, status")
      .eq("pharmacy_id", selectedPharmacy!.id)
      .gte("created_at", since.toISOString());

    const points: MonthPoint[] = [];
    for (let i = months - 1; i >= 0; i--) {
      const monthStart = startOfMonth(subMonths(new Date(), i));
      const monthEnd = endOfMonth(monthStart);
      const label = format(monthStart, "MMM", { locale: nl });
      const nieuw = (data ?? []).filter((c) => {
        const d = parseISO(c.created_at);
        return d >= monthStart && d <= monthEnd;
      }).length;
      const opgelost = (data ?? []).filter((c) => {
        if (!c.resolved_at) return false;
        const d = parseISO(c.resolved_at);
        return d >= monthStart && d <= monthEnd;
      }).length;
      points.push({ month: label, nieuw, opgelost });
    }
    setComplaintTrend(points);
  };

  const fetchCalibrationStats = async () => {
    const since = subMonths(new Date(), 3);
    const { data } = await supabase
      .from(Tables.CALIBRATION_RECORDS)
      .select("status")
      .eq("pharmacy_id", selectedPharmacy!.id)
      .gte("calibration_date", format(since, "yyyy-MM-dd"));

    const passed = (data ?? []).filter((r) => r.status === "passed").length;
    const adjustment = (data ?? []).filter((r) => r.status === "needs_adjustment").length;
    const failed = (data ?? []).filter((r) => r.status === "failed").length;
    const total = passed + adjustment + failed;

    if (total === 0) {
      setCalibrationStats([]);
      return;
    }

    setCalibrationStats([
      { name: "Geslaagd", value: passed, color: "#16a34a" },
      { name: "Aanpassing nodig", value: adjustment, color: "#d97706" },
      { name: "Niet geslaagd", value: failed, color: "#dc2626" },
    ]);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="h-40 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const totalCal = calibrationStats.reduce((s, c) => s + c.value, 0);
  const passedCal = calibrationStats.find((c) => c.name === "Geslaagd")?.value ?? 0;
  const passRate = totalCal > 0 ? Math.round((passedCal / totalCal) * 100) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* Task completion trend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Voltooide taken / week</h3>
          <div className="flex gap-1">
            {(["8w", "12w"] as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => setTaskRange(r)}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                  taskRange === r ? "bg-primary-100 text-primary-700" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        {taskTrend.every((p) => p.voltooid === 0) ? (
          <div className="h-40 flex items-center justify-center text-xs text-gray-400">
            Geen voltooide taken in deze periode
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={taskTrend} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                cursor={{ fill: "#f9fafb" }}
              />
              <Bar dataKey="voltooid" fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Complaint trend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Klachten per maand</h3>
          <div className="flex gap-1">
            {(["3m", "6m"] as MonthRange[]).map((r) => (
              <button
                key={r}
                onClick={() => setComplaintRange(r)}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                  complaintRange === r ? "bg-primary-100 text-primary-700" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        {complaintTrend.every((p) => p.nieuw === 0 && p.opgelost === 0) ? (
          <div className="h-40 flex items-center justify-center text-xs text-gray-400">
            Geen klachten in deze periode
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={complaintTrend} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                cursor={{ fill: "#f9fafb" }}
              />
              <Bar dataKey="nieuw" name="Nieuw" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Bar dataKey="opgelost" name="Opgelost" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Calibration pass rate */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Kalibratie resultaten</h3>
          <span className="text-xs text-gray-400">Laatste 3 maanden</span>
        </div>
        {totalCal === 0 ? (
          <div className="h-40 flex items-center justify-center text-xs text-gray-400">
            Geen kalibraties in deze periode
          </div>
        ) : (
          <div className="flex items-center">
            <ResponsiveContainer width="60%" height={160}>
              <PieChart>
                <Pie
                  data={calibrationStats}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={65}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {calibrationStats.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {passRate !== null && (
                <div className="text-center mb-3">
                  <span className="text-2xl font-bold text-gray-900">{passRate}%</span>
                  <p className="text-xs text-gray-500">Slagingspercentage</p>
                </div>
              )}
              {calibrationStats.map((s) => (
                <div key={s.name} className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-xs text-gray-600 truncate">{s.name}</span>
                  <span className="text-xs font-medium text-gray-900 ml-auto">{s.value}</span>
                </div>
              ))}
              <p className="text-xs text-gray-400 pt-1">{totalCal} totaal</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiCharts;
